import { Col, Form, Row } from 'antd';
import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UserId } from 'api/models';
import { TimesheetEntryType } from 'api/timesheets';
import { Button, ButtonGroup } from 'components/atoms/button';
import { Input } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import {
  Drawer,
  DrawerContent,
} from 'components/organisms/timesheets/common/drawer';
import { useTranslate } from 'hooks/use-translation';
import { TimesheetWeeklyRow } from '../../types';
import { Content } from '../components';

const FormContainer = styled.div`
  padding: var(--spacing-05);
`;

const StyledDrawerContent = styled(DrawerContent.Content)`
  height: calc(100vh - 60px - 89px);
`;

export const EditAdHocDrawer: FC<{
  visible: boolean;
  editDetails: TimesheetWeeklyRow | undefined;
  onClose: () => void;
  editAdhocTaskName: (
    timesheetRows: TimesheetWeeklyRow,
    activityName: string,
    userId: UserId,
  ) => void;
  userId: UserId;
}> = ({ visible, onClose, editDetails, editAdhocTaskName, userId }) => {
  const [form] = Form.useForm();
  const translate = useTranslate();

  const handleClose = () => {
    onClose();
    form.resetFields();
  };

  const onSubmit = async () => {
    const { activityName } = await form.validateFields();

    editAdhocTaskName(editDetails as TimesheetWeeklyRow, activityName, userId);
    handleClose();
  };

  useEffect(() => {
    form.resetFields();
  });

  const nonProjectActivity = translate(
    'timesheets.labels.non_project_activity',
  );

  return (
    <Drawer visible={visible} onClose={handleClose} destroyOnClose>
      <Content>
        <DrawerContent.Title>
          <FormattedMessage id="timesheets.title.adhoc_activity" />
        </DrawerContent.Title>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            activityName:
              editDetails?.task.entryType === TimesheetEntryType.AdhocEntry &&
              editDetails?.task.activityName,
          }}
        >
          <StyledDrawerContent>
            <FormContainer>
              <Row>
                <Col span={24}>
                  <Form.Item
                    label={<FormattedMessage id="projects.list.projectName" />}
                    name="project"
                    required
                  >
                    <Select
                      type="user"
                      styles={{
                        control: (css) => ({
                          ...css,
                          pointerEvents: 'auto',
                          cursor: 'not-allowed',
                        }),
                      }}
                      isDisabled
                      placeholder={
                        editDetails?.task.project?.name ?? nonProjectActivity
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Form.Item
                    label={
                      <FormattedMessage id="timesheets.labels.activity_name" />
                    }
                    name="activityName"
                    data-cy="inputs.activity-name"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a valid text',
                      },
                    ]}
                  >
                    <Input autoComplete="off" />
                  </Form.Item>
                </Col>
              </Row>
            </FormContainer>
          </StyledDrawerContent>
          <DrawerContent.Footer>
            <ButtonGroup>
              <Button onClick={handleClose}>
                <FormattedMessage id="cancel" />
              </Button>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    disabled={
                      !form.isFieldsTouched() ||
                      form
                        .getFieldsError()
                        .filter(({ errors }) => errors.length).length > 0
                    }
                    type="primary"
                    htmlType="submit"
                    onClick={onSubmit}
                  >
                    <FormattedMessage id="timesheets.buttons.edit_adhoc_activity" />
                  </Button>
                )}
              </Form.Item>
            </ButtonGroup>
          </DrawerContent.Footer>
        </Form>
      </Content>
    </Drawer>
  );
};
